import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  Tag,
  Typography,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import pallete from "../../../utils/pallete";
import VerificationHistoryTable from "./VerificationHistoryTable";

export default function LegalitySection({
  legalityData,
  legalityVerification,
  isVerifying,
  isRejecting,
  isRevising,
  isAllowedAction,
  onVerify,
  onReject,
  onCancel,
  onPreview,
  onDownload,
}) {
  const breakpoint = Grid.useBreakpoint();

  const labelStyle = {
    width: breakpoint.lg ? 260 : "100%",
    minWidth: 260,
  };

  const contentStyle = {
    width: breakpoint.lg ? "calc(100% - 260px)" : "100%",
  };

  const descriptionTitle = (title) => (
    <Typography.Title
      level={2}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 16,
      }}
    >
      {title}
    </Typography.Title>
  );

  const statusTitle = (title) => (
    <Typography.Title
      level={3}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 16,
      }}
    >
      {title}
    </Typography.Title>
  );

  const dataLegalitasItems = [
    {
      label: "Alamat Email Terdaftar",
      children: (
        <Flex vertical gap={4}>
          <Typography.Text>{legalityData?.email || "-"}</Typography.Text>
        </Flex>
      ),
    },
  ];

  const identitasLembagaItems = [
    {
      label: "Logo Lembaga",
      children: legalityData?.attachments?.logo?.fileName ? (
        <Flex gap={8} align="center">
          <FileTextOutlined />
          <Typography.Text style={{ marginRight: "auto" }}>
            {legalityData?.attachments.logo.fileName}
          </Typography.Text>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onDownload(
                legalityData?.attachments.logo.filePath,
                legalityData?.attachments.logo.fileName || "logo",
              )
            }
          >
            Unduh
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => onPreview(legalityData?.attachments.logo.filePath)}
          >
            Lihat
          </Button>
        </Flex>
      ) : (
        "-"
      ),
      span: 2,
    },
    {
      label: "Nama Lembaga",
      children: legalityData?.name || "-",
      span: 2,
    },
    {
      label: "Jenis Lembaga",
      children: legalityData?.type || "-",
    },
    {
      label: "Bentuk Lembaga",
      children: legalityData?.institutionForm || "-",
    },
    {
      label: "Status Lembaga",
      children: legalityData?.institutionStatus || "-",
    },
    {
      label: "Metode Pembayaran dengan K/L Pemerintah",
      children: legalityData?.paymentMethod || "-",
    },
    // legalityData?.isSMK && {
    //   label: "Jurusan / Program Studi SMK",
    //   children: legalityData?.smkMajor?.join(", ") || "-",
    //   span: 2,
    // },

    // {
    //   label: "Sertifikasi Lembaga",
    //   span: 2,
    //   children: legalityData?.attachments?.certification?.fileName ? (
    //     <Flex gap={8} align="center">
    //       <FileTextOutlined />
    //       <Typography.Text style={{ marginRight: "auto" }}>
    //         {legalityData?.attachments.certification.fileName}
    //       </Typography.Text>
    //       <Button
    //         variant="outlined"
    //         color="primary"
    //         size="small"
    //         onClick={() =>
    //           onDownload(
    //             legalityData?.attachments.certification.filePath,
    //             legalityData?.attachments.certification.fileName ||
    //               "sertifikat",
    //           )
    //         }
    //       >
    //         Unduh
    //       </Button>
    //       <Button
    //         variant="outlined"
    //         color="primary"
    //         size="small"
    //         onClick={() =>
    //           onPreview(legalityData?.attachments.certification.filePath)
    //         }
    //       >
    //         Lihat
    //       </Button>
    //     </Flex>
    //   ) : (
    //     "-"
    //   ),
    // },
  ].filter(Boolean);

  const pengelolaLembagaItems = [
    {
      label: "Pimpinan Lembaga",
      children: legalityData?.leaderName || "-",
    },
    {
      label: "Jabatan Pimpinan",
      children: legalityData?.leaderPosition || "-",
    },
    {
      label: "Kapasitas Latih",
      children: legalityData?.trainingCapacity || "-",
      span: 2,
    },
    {
      label: "Afiliasi / Jejaring Lembaga",
      children: legalityData?.institutionalAffiliationName || "-",
      span: 2,
    },
  ];

  // One "Sektor Spesialisasi" row per sector, with its positions next to it.
  const groupedSectorals = (legalityData?.sectorals || []).reduce(
    (acc, curr) => {
      if (!acc[curr.sectoral]) acc[curr.sectoral] = [];
      acc[curr.sectoral].push(curr.position);
      return acc;
    },
    {},
  );

  const informasiSektorJabatanItems =
    Object.keys(groupedSectorals).length > 0
      ? Object.entries(groupedSectorals).flatMap(([sectoral, positions]) => [
          {
            label: "Sektor Spesialisasi",
            children: sectoral || "-",
          },
          {
            label: "Jabatan",
            children: (
              <Flex wrap="wrap" gap={8}>
                {positions.filter(Boolean).map((pos, idx) => (
                  <Tag key={idx} color="blue" style={{ margin: 0 }}>
                    {pos}
                  </Tag>
                ))}
              </Flex>
            ),
          },
        ])
      : [
          { label: "Sektor Spesialisasi", children: "-" },
          { label: "Jabatan", children: "-" },
        ];

  const smkMajors = legalityData?.smkMajor || [];
  const informasiJurusanItems =
    smkMajors.length > 0
      ? smkMajors.flatMap((major) => [
          { label: "Jurusan", children: major?.name || "-" },
          {
            label: "Jumlah Siswa Tersedia",
            children: major?.studentQty ?? "-",
          },
        ])
      : [
          { label: "Jurusan", children: "-" },
          { label: "Jumlah Siswa Tersedia", children: "-" },
        ];

  const alamatItems = [
    {
      label: "Alamat Lengkap",
      children: legalityData?.address || "-",
      span: 2,
    },
    {
      label: "Provinsi",
      children: legalityData?.province || "-",
    },
    {
      label: "Kota/Kabupaten",
      children: legalityData?.district || "-",
    },
    {
      label: "Kode Pos",
      span: 2,
      children: legalityData?.postalCode || "-",
    },
  ];

  const kontakItems = [
    {
      label: "Nomor Telepon",
      children: legalityData?.phone || "-",
      span: 2,
    },
    {
      label: "Link Website / Sosial Media Lembaga",
      children: legalityData?.website || "-",
    },
    {
      label: "Link INAPROC",
      children: legalityData?.inaprocLink || "-",
    },
  ];

  const picItems = [
    {
      label: "Nama Lengkap",
      children: legalityData?.pic?.fullName || "-",
      span: 2,
    },
    {
      label: "Jabatan",
      children: legalityData?.pic?.position || "-",
    },
    {
      label: "Nomor Telepon",
      children: legalityData?.pic?.phone || "-",
    },
  ];

  const informasiAkreditasiLembagaItems = [
    {
      label: "Akreditasi Lembaga",
      span: 2,
      children: legalityData?.attachments?.certification?.filePath ? (
        <Flex align="center" gap={8}>
          <Typography.Text style={{ marginRight: "auto" }} ellipsis>
            {legalityData.attachments.certification.fileName}
          </Typography.Text>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onDownload(
                legalityData.attachments.certification.filePath,
                legalityData.attachments.certification.fileName ||
                  "sertifikat-akreditasi",
              )
            }
          >
            Unduh
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onPreview(legalityData.attachments.certification.filePath)
            }
          >
            Lihat
          </Button>
        </Flex>
      ) : (
        "-"
      ),
    },
    {
      label: "Dokumen Sertifikasi Lembaga",
      span: 2,
      children: legalityData?.attachments?.certificationDocument?.filePath ? (
        <Flex align="center" gap={8}>
          <Typography.Text style={{ marginRight: "auto" }} ellipsis>
            {legalityData.attachments.certificationDocument.fileName}
          </Typography.Text>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onDownload(
                legalityData.attachments.certificationDocument.filePath,
                legalityData.attachments.certificationDocument.fileName ||
                  "sertifikasi-lembaga",
              )
            }
          >
            Unduh
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onPreview(legalityData.attachments.certificationDocument.filePath)
            }
          >
            Lihat
          </Button>
        </Flex>
      ) : (
        "-"
      ),
    },
    {
      label: "Nomor Akreditasi",
      children: legalityData?.certificationNumber || "-",
      span: 2,
    },
    {
      label: "Tanggal Akreditasi",
      children: legalityData?.certificationDate || "-",
    },
    {
      label: "Tanggal Akhir Akreditasi",
      children: legalityData?.accreditationEndDate || "-",
    },
    {
      label: "Terakreditasi",
      children: legalityData?.accreditationStatus || "-",
      span: 2,
    },
  ];

  const legalityTypeLabels = {
    operational_license: "Izin Operasional",
    nib: "NIB",
    oss: "OSS",
  };

  const operationalLembagaItems = [
    {
      label: "Data Legalitas Lembaga (Izin Operasional/NIB/OSS)",
      span: 2,
      children: legalityData?.attachments?.legalityDocument?.filePath ? (
        <Flex align="center" gap={8}>
          <Typography.Text style={{ marginRight: "auto" }} ellipsis>
            {legalityData.attachments.legalityDocument.fileName}
          </Typography.Text>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onDownload(
                legalityData.attachments.legalityDocument.filePath,
                legalityData.attachments.legalityDocument.fileName ||
                  "dokumen-legalitas",
              )
            }
          >
            Unduh
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              onPreview(legalityData.attachments.legalityDocument.filePath)
            }
          >
            Lihat
          </Button>
        </Flex>
      ) : (
        "-"
      ),
    },
    {
      label: "Nomor Izin Operasional",
      children: legalityData?.operationalLicenseNumber || "-",
    },
    {
      label: "Tanggal Terbit Izin",
      children: legalityData?.publicationDate || "-",
    },
    {
      label: "Lembaga yang Mengeluarkan Izin",
      children: legalityData?.licensingAgency || "-",
    },
  ];

  return (
    <>
      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi Akun Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={dataLegalitasItems}
      />

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi Profil Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={identitasLembagaItems}
      />

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi Pengelola Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={pengelolaLembagaItems}
      />

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle(
          "Informasi Sektor dan Jabatan Spesialisasi Lembaga",
        )}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={informasiSektorJabatanItems}
      />

      {legalityData?.isSMK && (
        <Descriptions
          style={{ marginBottom: 24 }}
          column={breakpoint.lg ? 2 : 1}
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          bordered
          size="small"
          title={descriptionTitle("Informasi Jurusan & Jumlah Siswa Tersedia")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={informasiJurusanItems}
        />
      )}

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi Alamat Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={alamatItems}
      />

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi Kontak Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={kontakItems}
      />

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi PIC Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={picItems}
      />

      <Descriptions
        style={{
          marginBottom: legalityData?.isAccreditationExpiringSoon ? 12 : 24,
        }}
        column={breakpoint.lg ? 2 : 1}
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        bordered
        size="small"
        title={descriptionTitle("Informasi Akreditasi Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={informasiAkreditasiLembagaItems}
      />
      {legalityData?.isAccreditationExpiringSoon && (
        <Alert
          style={{ marginBottom: 24 }}
          type="warning"
          showIcon
          message="Catatan: Masa berlaku akreditasi lembaga akan segera berakhir. Mohon segera tindak lanjuti kepada lembaga terkait."
        />
      )}

      <Descriptions
        style={{ marginBottom: 24 }}
        column={breakpoint.lg ? 2 : 1}
        bordered
        styles={{
          label: labelStyle,
          content: contentStyle,
        }}
        size="small"
        title={descriptionTitle("Informasi Izin Operasional Lembaga")}
        layout={breakpoint.lg ? "horizontal" : "vertical"}
        items={operationalLembagaItems}
      />

      <div style={{ marginBottom: 24, paddingTop: 24 }}>
        <Card title={statusTitle("Status Verifikasi")}>
          <VerificationHistoryTable
            history={legalityData?.verificationStatusses || []}
          />
        </Card>
      </div>

      <Flex justify="flex-end" gap={12}>
        {legalityVerification?.status !== "accepted" &&
          legalityVerification?.status !== "rejected" &&
          isAllowedAction && (
            <>
              <Button
                type="primary"
                loading={isVerifying}
                disabled={isRevising || isRejecting}
                style={{
                  backgroundColor: pallete.success.variant,
                  fontWeight: 600,
                }}
                onClick={onVerify}
              >
                Terima
              </Button>

              <Button
                danger
                loading={isRejecting}
                disabled={isVerifying || isRevising}
                style={{
                  fontWeight: 600,
                  backgroundColor: pallete.danger.main,
                  color: pallete.background.white,
                }}
                onClick={onReject}
              >
                Tolak
              </Button>
            </>
          )}
      </Flex>
    </>
  );
}
